import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EditorPageLayout from "../../../../components/layouts/editor-page/editor-page-layout";
import ListLayout from "../../../../components/layouts/list-layout/list-layout";
import { ConfirmModal } from "../../../../components/modal/components/confirm-modal/confirm-modal";
import useNavigationBlocker from "../../../../hooks/navigation-blocker-hook";
import useValidationGroup from "../../../../hooks/validation-group/validation-group-hook";
import AccountDetailsCard from "../account-details-card/account-details-card";
import AccountPermissionsCard from "../account-permissions-card/account-permissions-card";
import accountPagedListStore from "../../stores/account-list-store";
import currentAccountStore from "../../stores/current-account-store";
import { PERMISSION } from "../../../../models/PermissionsTypes";
import usePage from "../../../../components/layouts/base-page-layout/page-hook";
import InvitationStatusModal from "../invitation-status-modal/invitation-status-modal";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";


export interface AccountEditorPageProps {
    children?: React.ReactNode;
}

const AccountEditor: React.FC<AccountEditorPageProps> = observer(() => {
    const { id } = useParams();
    const store = accountPagedListStore.currentAccount;
    const isNew = store?.isNew
    const vGroup = useValidationGroup();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const { setSubTitles } = usePage();

    useEffect(() => {
        accountPagedListStore.setCurrentAccount(id as string);
        return () => {
            accountPagedListStore.dropCurrentAccount();
        };
    }, [id]);

    const firstName = store?.first_name;
    const lastName = store?.surname;
    useEffect(() => {
        if (firstName && lastName) {
            setSubTitles([`${firstName} ${lastName}`]);
        }
    }, [firstName, lastName, setSubTitles])

    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const hasChanges = (): boolean => {
        return store?.hasChanges() || false;
    }

    const discardChanges = () => {
        store?.discardChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );

    const [newlyInvitedId, setNewlyInvitedId] = useState<string | number | undefined>(undefined);
    const saveChanges = async () => {
        await store?.save();
        /** was new and invitation complete */
        if (isNew && !store.isNew) {
            setNewlyInvitedId(store.id);
        }
    }

    const onInvitationStatusCloseClick = () => {
        unblockNavigation();
        const newPath = `${pathname}/../${newlyInvitedId}`;
        setNewlyInvitedId(undefined);
        navigate({
            pathname: newPath
        }, { replace: true });
    }

    return (
        <EditorPageLayout
            toolbar={{
                hasLocalesSelector: false,
                saveButton: {
                    hasChanges: hasChanges(),
                    onClick: () => saveChanges(),
                    disabled: !vGroup.isValid() || !hasChanges() || store?.isSaving(),
                },
                isLoading: store?.isSaving() || accountPagedListStore.isLoading || dashboardTranslationsStore.isLoading
            }}>
            <ListLayout>
                <AccountDetailsCard
                    account={store}
                    validationGroup={vGroup}
                    resetPasswordEnabled={store?.isMyAccount ||
                        currentAccountStore.hasPermission(PERMISSION.CHANGE_PASSWORD_TO_OTHER_USERS)
                    }
                />
                <AccountPermissionsCard account={store} disabled={!store?.canUpdateAccountSettings} />

            </ListLayout>
            {isShowUnsaved &&
                <ConfirmModal
                    cancelButton={{
                        title: 'Cancel',
                        onClick: () => setIsShowUnsaved(false)
                    }}
                    okButton={{
                        title: 'Discard',
                        onClick: () => {
                            discardChanges();
                            setIsShowUnsaved(false);
                            retryNavigation();
                        }
                    }}
                />
            }
            {newlyInvitedId && (
                <InvitationStatusModal
                    successful
                    onClose={onInvitationStatusCloseClick} />
            )}
        </EditorPageLayout>
    );
});

export default AccountEditor;
